import { unsafeGet, drop, of, isNonEmpty, headNonEmpty, tailNonEmpty, toReadonlyArray, unsafeFromArray, appendAll, prepend } from '../../Chunk/dist/effect-Chunk.esm.js';
import { Structural } from '../Data.esm.js';
import { equals } from '../../Equal/dist/effect-Equal.esm.js';
import { pipe, dual } from '../../Function/dist/effect-Function.esm.js';

/** @internal */
const ChunkPatchTypeId = /*#__PURE__*/Symbol.for("effect/DifferChunkPatch");
function variance(a) {
  return a;
}
const PatchProto = {
  ...Structural.prototype,
  [ChunkPatchTypeId]: {
    _Value: variance,
    _Patch: variance
  }
};
const EmptyProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "Empty"
});
const _empty = /*#__PURE__*/Object.create(EmptyProto);

/**
 * @internal
 */
const empty = () => _empty;
const AndThenProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "AndThen"
});
const makeAndThen = (first, second) => {
  const o = Object.create(AndThenProto);
  o.first = first;
  o.second = second;
  return o;
};
const AppendProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "Append"
});
const makeAppend = values => {
  const o = Object.create(AppendProto);
  o.values = values;
  return o;
};
const SliceProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "Slice"
});
const makeSlice = (from, until) => {
  const o = Object.create(SliceProto);
  o.from = from;
  o.until = until;
  return o;
};
const UpdateProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "Update"
});
const makeUpdate = (index, patch) => {
  const o = Object.create(UpdateProto);
  o.index = index;
  o.patch = patch;
  return o;
};
/** @internal */
const diff = options => {
  let i = 0;
  let patch = empty();
  while (i < options.oldValue.length && i < options.newValue.length) {
    const oldElement = unsafeGet(i)(options.oldValue);
    const newElement = unsafeGet(i)(options.newValue);
    const valuePatch = options.differ.diff(oldElement, newElement);
    if (!equals(valuePatch, options.differ.empty)) {
      patch = pipe(patch, combine(makeUpdate(i, valuePatch)));
    }
    i = i + 1;
  }
  if (i < options.oldValue.length) {
    patch = pipe(patch, combine(makeSlice(0, i)));
  }
  if (i < options.newValue.length) {
    patch = pipe(patch, combine(makeAppend(drop(i)(options.newValue))));
  }
  return patch;
};

/** @internal */
const combine = /*#__PURE__*/dual(2, (self, that) => makeAndThen(self, that));

/** @internal */
const patch = /*#__PURE__*/dual(3, (self, oldValue, differ) => {
  let chunk = oldValue;
  let patches = of(self);
  while (isNonEmpty(patches)) {
    const head = headNonEmpty(patches);
    const tail = tailNonEmpty(patches);
    switch (head._tag) {
      case "Empty":
        {
          patches = tail;
          break;
        }
      case "AndThen":
        {
          patches = prepend(head.first)(prepend(head.second)(tail));
          break;
        }
      case "Append":
        {
          chunk = appendAll(head.values)(chunk);
          patches = tail;
          break;
        }
      case "Slice":
        {
          const array = toReadonlyArray(chunk);
          chunk = unsafeFromArray(array.slice(head.from, head.until));
          patches = tail;
          break;
        }
      case "Update":
        {
          const array = toReadonlyArray(chunk);
          array[head.index] = differ.patch(head.patch, array[head.index]);
          chunk = unsafeFromArray(array);
          patches = tail;
          break;
        }
    }
  }
  return chunk;
});

export { ChunkPatchTypeId, combine, diff, empty, patch };
